import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Share2, Check, Loader2, AlertTriangle } from 'lucide-react';

export default function AcceptSharePage() {
  const { shareCode } = useParams();
  const { authHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, valid, accepted, error
  const [shareInfo, setShareInfo] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/horses/share/validate/${shareCode}`)
      .then(res => {
        setShareInfo(res.data);
        setStatus('valid');
      })
      .catch(() => setStatus('error'));
  }, [API, shareCode]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/horses/share/accept/${shareCode}`, {}, authHeaders());
      setStatus('accepted');
      toast.success(`"${res.data.horse_name}" has been added to your dashboard!`);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept share');
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(27,67,50,0.08)] text-center" data-testid="accept-share-card">
          {status === 'loading' && (
            <div className="py-8">
              <Loader2 size={32} className="text-[#4B6B58] mx-auto animate-spin mb-4" />
              <p className="text-[#4B6B58]">Validating share link...</p>
            </div>
          )}

          {status === 'valid' && shareInfo && (
            <div className="py-4">
              <div className="w-16 h-16 rounded-full bg-[#E9EFE8] flex items-center justify-center mx-auto mb-5">
                <Share2 size={28} className="text-[#2D6A4F]" />
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2" data-testid="share-horse-name">
                {shareInfo.horse_name}
              </h1>
              <p className="text-[#4B6B58] mb-1">
                <span className="font-semibold text-[#122A1F]">{shareInfo.owner_name}</span> wants to share this horse's records with you.
              </p>
              <p className="text-xs text-[#4B6B58] mb-6">You'll be able to view all records and add new ones.</p>

              <button
                onClick={handleAccept}
                disabled={accepting}
                data-testid="accept-share-button"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
              >
                {accepting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                {accepting ? 'Accepting...' : 'Accept & Add to Dashboard'}
              </button>
            </div>
          )}

          {status === 'accepted' && (
            <div className="py-8">
              <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-5">
                <Check size={28} className="text-[#065F46]" />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">Horse Added!</h2>
              <p className="text-[#4B6B58]">Redirecting to your dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <div className="w-16 h-16 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-5">
                <AlertTriangle size={28} className="text-[#991B1B]" />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">Link Expired</h2>
              <p className="text-[#4B6B58] mb-6">This share link is no longer valid or has already been used.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
